import { useState } from 'react';
import { Outlet, NavLink, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Upload, Table, Users as UsersIcon, DollarSign, UserCog, LayoutGrid, Building2, LayoutDashboard, TrendingUp, CircleDollarSign } from 'lucide-react';
import type { System } from '../lib/api';
import Header from './Header';
import { useIsAgent, useIsAdmin } from '../store/authStore';

export default function AppLayout() {
  const { sys } = useParams<{ sys: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isAgent = useIsAgent();
  const isAdmin = useIsAdmin();
  const [_sidebarOpen, _setSidebarOpen] = useState(false);

  // Derive currentSys from URL
  const currentSys = (sys === 'messer' ? 'Messer' : sys === 'empire-aca' ? 'Empire-ACA' : null) as
    | System
    | null;
  // Sidebar nav still needs a sys for its links on sys-agnostic admin
  // routes like /users and /search. Default to Messer when there's no
  // :sys in the URL.
  const sidebarSys: System = currentSys ?? 'Messer';

  const switchSystem = (next: System) => {
    const slug = next === 'Messer' ? 'messer' : 'empire-aca';
    // Try to keep them on the same page when switching
    const subpath = location.pathname.split('/').slice(2).join('/') || 'policy-data';
    navigate(`/${slug}/${subpath}`);
  };

  // Agent layout — drastically simpler nav
  const isOnAgentRoute = location.pathname.startsWith('/my');

  // Authorization guard: a non-admin agent may only see the /my portal. If
  // they land on any admin route (by URL, stale link, or login redirect),
  // bounce them to their dashboard. Admins are unaffected; an admin who is
  // impersonating views /my routes, so they're never bounced mid-preview.
  if (isAgent && !isAdmin && !isOnAgentRoute) {
    return <Navigate to="/my/dashboard" replace />;
  }

  return (
    <div className="flex flex-col h-screen" style={{ backgroundColor: 'var(--color-bg)' }}>
      <Header currentSys={currentSys} switchSystem={switchSystem} />

      <div className="flex flex-1 overflow-hidden relative">
        <aside className="w-56 sidebar flex flex-col shrink-0 hidden md:flex">
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {(isAgent || isOnAgentRoute) && !isAdmin ? (
              <>
                <NavItem to="/my/dashboard" icon={<LayoutDashboard size={18} />} label="Dashboard" />
                <NavItem to="/my/payments" icon={<DollarSign size={18} />} label="My Payments" />
                <NavItem to="/my/carriers" icon={<Building2 size={18} />} label="My Carriers" />
              </>
            ) : isAdmin || currentSys ? (
              <>
                <NavItem to="/cycle" icon={<CircleDollarSign size={18} />} label="Pay Cycle" />
                <hr
                  style={{
                    border: 'none',
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                    margin: '6px 0',
                  }}
                />
                <NavItem
                  to={`/${slugFor(sidebarSys)}/policy-data`}
                  icon={<LayoutGrid size={18} />}
                  label="Policy Data"
                />
                <NavItem
                  to={`/${slugFor(sidebarSys)}/imports`}
                  icon={<Upload size={18} />}
                  label="Imports"
                />
                <NavItem
                  to={`/${slugFor(sidebarSys)}/carriers`}
                  icon={<Table size={18} />}
                  label="Carriers"
                />
                <NavItem
                  to={`/${slugFor(sidebarSys)}/pay-runs`}
                  icon={<DollarSign size={18} />}
                  label="Pay Runs"
                />
                <NavItem
                  to={`/${slugFor(sidebarSys)}/reports`}
                  icon={<TrendingUp size={18} />}
                  label="Reports"
                />
                <NavItem
                  to={`/${slugFor(sidebarSys)}/agents`}
                  icon={<UsersIcon size={18} />}
                  label="Agents"
                />
                <hr
                  style={{
                    border: 'none',
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                    margin: '6px 0',
                  }}
                />
                <NavItem to="/users" icon={<UserCog size={18} />} label="Users" />
              </>
            ) : null}
          </nav>
        </aside>

        <main className="flex-1 overflow-auto" style={{ backgroundColor: 'var(--color-bg)' }}>
          <div className="p-4 md:p-6 max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

function NavItem({ to, icon, label }: { to: string; icon: React.ReactNode; label: string }) {
  return (
    <NavLink
      to={to}
      end={false}
      className={({ isActive }) =>
        `nav-item ${isActive ? 'nav-item-active' : 'nav-item-inactive'}`
      }
    >
      {icon}
      <span className="font-medium text-sm">{label}</span>
    </NavLink>
  );
}

function slugFor(s: System): string {
  return s === 'Messer' ? 'messer' : 'empire-aca';
}
